import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { readApiErrors } from '@/components/features/courses/courseUtils'
import { sdk } from '@/lib/sdk'

const MAX_POINTS = 100_000
const REASON_MAX = 200

/**
 * F-153 "Ajustar pontos" (owner and admin), from the profile and from the
 * ranking: a positive number adds, a negative one takes away (0 is refused),
 * and the reason goes to the person's points history. The API's field errors
 * land under their field.
 */
export const AdjustPointsDialog = ({ open, onOpenChange, memberId, memberName, onSaved }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  memberId: string
  memberName: string
  /** The new total, when the API says it. */
  onSaved: (totalPoints?: number) => void
}) => {
  const { t } = useTranslation('badges')
  const [points, setPoints] = React.useState('')
  const [reason, setReason] = React.useState('')
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [formError, setFormError] = React.useState('')
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    setPoints('')
    setReason('')
    setErrors({})
    setFormError('')
  }, [open])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (saving) return
    const n = Number(points)
    const next: Record<string, string> = {}
    if (!/^-?\d+$/.test(points.trim()) || n === 0 || Math.abs(n) > MAX_POINTS) next.points = t('points.errors.points', { max: MAX_POINTS.toLocaleString() })
    if (!reason.trim()) next.reason = t('points.errors.reasonRequired')
    else if (reason.trim().length > REASON_MAX) next.reason = t('points.errors.reasonTooLong', { max: REASON_MAX })
    setErrors(next)
    setFormError('')
    if (Object.keys(next).length) return
    setSaving(true)
    try {
      const out = await sdk.gamification.adjustPoints({ memberId, points: n, reason: reason.trim() })
      if (out?.skipped) {
        setFormError(t('points.gamificationOff'))
        return
      }
      onSaved(typeof out?.totalPoints === 'number' ? out.totalPoints : undefined)
      onOpenChange(false)
    } catch (err) {
      const { fields, message } = readApiErrors(err)
      const known = { points: fields.points, reason: fields.reason }
      if (known.points || known.reason) setErrors(Object.fromEntries(Object.entries(known).filter(([, v]) => v)) as Record<string, string>)
      else setFormError(fields.memberId || message || t('points.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-1.5rem)] max-w-md overflow-y-auto p-4 sm:p-6" data-adjust-points-dialog>
        <DialogHeader>
          <DialogTitle>{t('points.title')}</DialogTitle>
          <DialogDescription>{t('points.description', { name: memberName })}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="min-w-0 space-y-4">
          <div className="grid gap-1.5">
            <label htmlFor="adjust-points" className="text-sm font-medium">{t('points.amount')}</label>
            <Input id="adjust-points" type="number" inputMode="numeric" step={1} min={-MAX_POINTS} max={MAX_POINTS} value={points} disabled={saving}
              className="max-w-[10rem]" aria-invalid={Boolean(errors.points)} autoFocus data-adjust-points-amount
              onChange={(e) => { setPoints(e.target.value); setErrors((p) => ({ ...p, points: '' })) }} />
            {errors.points
              ? <p className="text-xs text-red-600">{errors.points}</p>
              : <p className="text-xs text-gray-500">{t('points.amountHint')}</p>}
          </div>
          <div className="grid gap-1.5">
            <label htmlFor="adjust-reason" className="text-sm font-medium">{t('points.reason')}</label>
            <Textarea id="adjust-reason" rows={3} value={reason} maxLength={REASON_MAX} disabled={saving} aria-invalid={Boolean(errors.reason)}
              placeholder={t('points.reasonPlaceholder')} data-adjust-points-reason
              onChange={(e) => { setReason(e.target.value); setErrors((p) => ({ ...p, reason: '' })) }} />
            {errors.reason
              ? <p className="text-xs text-red-600">{errors.reason}</p>
              : <p className="text-xs text-gray-500">{t('points.reasonHint', { length: reason.length, max: REASON_MAX })}</p>}
          </div>
          {formError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{formError}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => onOpenChange(false)} disabled={saving}>{t('common.cancel')}</Button>
            <Button type="submit" variant="secondary" className="w-full sm:w-auto" disabled={saving} data-adjust-points-save>
              {saving ? t('common.saving') : t('points.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
